// shared/api-client/endpoints/polls.js
// Polls API endpoints

import { baseApi } from "../baseApi";

const pollsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ========================================================================
    // MEMBER ENDPOINTS
    // ========================================================================

    getActivePolls: builder.query({
      query: () => "/api/polls",
      providesTags: (result) =>
        result?.data?.polls
          ? [
              ...result.data.polls.map(({ id }) => ({ type: "Poll", id })),
              { type: "Poll", id: "LIST" },
            ]
          : [{ type: "Poll", id: "LIST" }],
    }),

    getClosedPollResults: builder.query({
      query: () => "/api/polls/results",
      providesTags: [{ type: "Poll", id: "RESULTS" }],
    }),

    getPollById: builder.query({
      query: (id) => `/api/polls/${id}`,
      providesTags: (result, error, id) => [{ type: "Poll", id }],
    }),

    getMyPollResponse: builder.query({
      query: (pollId) => `/api/polls/${pollId}/my-response`,
      providesTags: (result, error, pollId) => [
        { type: "PollResponse", id: pollId },
      ],
    }),

    getPollResults: builder.query({
      query: (pollId) => `/api/polls/${pollId}/results`,
      providesTags: (result, error, pollId) => [
        { type: "Poll", id: `RESULTS_${pollId}` },
      ],
    }),

    submitPollResponse: builder.mutation({
      query: ({ pollId, answers }) => ({
        url: `/api/polls/${pollId}/responses`,
        method: "POST",
        body: { answers },
      }),
      invalidatesTags: (result, error, { pollId }) => [
        { type: "Poll", id: pollId },
        { type: "Poll", id: "LIST" },
        { type: "PollResponse", id: pollId },
        { type: "Poll", id: `RESULTS_${pollId}` },
      ],
    }),

    // ========================================================================
    // ADMIN ENDPOINTS
    // ========================================================================

    getAdminPolls: builder.query({
      query: (params = {}) => ({ url: "/api/polls/admin", params }),
      providesTags: (result) =>
        result?.data?.polls
          ? [
              ...result.data.polls.map(({ id }) => ({ type: "Poll", id })),
              { type: "Poll", id: "ADMIN_LIST" },
            ]
          : [{ type: "Poll", id: "ADMIN_LIST" }],
    }),

    createPoll: builder.mutation({
      query: (body) => ({ url: "/api/polls", method: "POST", body }),
      invalidatesTags: [
        { type: "Poll", id: "LIST" },
        { type: "Poll", id: "ADMIN_LIST" },
      ],
    }),

    updatePoll: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/api/polls/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Poll", id },
        { type: "Poll", id: "LIST" },
        { type: "Poll", id: "ADMIN_LIST" },
      ],
    }),

    deletePoll: builder.mutation({
      query: (id) => ({ url: `/api/polls/${id}`, method: "DELETE" }),
      invalidatesTags: (result, error, id) => [
        { type: "Poll", id },
        { type: "Poll", id: "LIST" },
        { type: "Poll", id: "ADMIN_LIST" },
      ],
    }),

    activatePoll: builder.mutation({
      query: (id) => ({ url: `/api/polls/${id}/activate`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [
        { type: "Poll", id },
        { type: "Poll", id: "LIST" },
        { type: "Poll", id: "ADMIN_LIST" },
      ],
    }),

    closePoll: builder.mutation({
      query: (id) => ({ url: `/api/polls/${id}/close`, method: "PATCH" }),
      invalidatesTags: (result, error, id) => [
        { type: "Poll", id },
        { type: "Poll", id: "LIST" },
        { type: "Poll", id: "ADMIN_LIST" },
        { type: "Poll", id: "RESULTS" },
      ],
    }),

    getPollRespondents: builder.query({
      query: (pollId) => `/api/polls/${pollId}/respondents`,
      providesTags: (result, error, pollId) => [
        { type: "PollResponse", id: `RESPONDENTS_${pollId}` },
      ],
    }),
  }),
});

// Member hooks
export const useGetActivePollsQuery = pollsApi.useGetActivePollsQuery;
export const useGetClosedPollResultsQuery =
  pollsApi.useGetClosedPollResultsQuery;
export const useGetPollByIdQuery = pollsApi.useGetPollByIdQuery;
export const useGetMyPollResponseQuery = pollsApi.useGetMyPollResponseQuery;
export const useGetPollResultsQuery = pollsApi.useGetPollResultsQuery;
export const useSubmitPollResponseMutation =
  pollsApi.useSubmitPollResponseMutation;

// Admin hooks
export const useGetAdminPollsQuery = pollsApi.useGetAdminPollsQuery;
export const useCreatePollMutation = pollsApi.useCreatePollMutation;
export const useUpdatePollMutation = pollsApi.useUpdatePollMutation;
export const useDeletePollMutation = pollsApi.useDeletePollMutation;
export const useActivatePollMutation = pollsApi.useActivatePollMutation;
export const useClosePollMutation = pollsApi.useClosePollMutation;
export const useGetPollRespondentsQuery = pollsApi.useGetPollRespondentsQuery;
